import { Avatar, AvatarGroup, HoverCard, HoverCardContent, HoverCardTrigger } from '@chain-zen/ui'
import React from 'react'

import type { UserDetails } from '../../../../../types/user'
import type { IWorkspace } from '../../../../../types/workspace'
import { userDisplayName } from '../../../../../utils/user'

interface IShipmentMember {
  members: UserDetails[]
  shipmentWorkspace: IWorkspace | null
}

const MemberList = ({ members }: { members: UserDetails[] }) => {
  return (
    <div className="flex flex-col gap-2 p-2">
      {members.map((member, index) => {
        const name = userDisplayName({
          email: member.email,
          name: member.name,
          phone: member.phone
        })

        return (
          <div key={`${member.email}-${index}`} className="flex items-center gap-2 p-2 rounded-md hover:bg-accent">
            <div className="flex items-center gap-2">
              <Avatar src={member.photo || undefined} alt={name} name={name} />
              <div className="flex flex-col">
                <span className="text-sm font-medium">{name}</span>
                <span className="text-xs text-muted-foreground">{member.email}</span>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

const WorkspaceFallback = ({ workspace }: { workspace: IWorkspace }) => {
  return (
    <div className="flex items-center gap-2 p-2">
      <img src={workspace?.logo || undefined} alt={workspace?.displayName || ''} className="h-6 w-6 rounded-full" />
      <span className="text-sm">{workspace?.displayName ?? ''}</span>
    </div>
  )
}

/**
 * Displays shipment members using an AvatarGroup with a hover card for detailed view.
 * Falls back to workspace display if no members are present.
 */
export const ShipmentMembers = ({ members, shipmentWorkspace }: IShipmentMember) => {
  const hasMembers = members.length > 0

  if (!hasMembers && shipmentWorkspace) {
    return <WorkspaceFallback workspace={shipmentWorkspace} />
  }

  return (
    <HoverCard>
      <HoverCardTrigger asChild={true}>
        <div>
          <AvatarGroup
            people={members.map((member) => ({
              email: member.email,
              name: userDisplayName({
                email: member.email,
                name: member.name,
                phone: member.phone
              }),
              online: member.online,
              photo: member.photo || ''
            }))}
            max={3}
            size="xs"
            showTooltip={false}
            variant="basic"
            badgeColor="bg-green-500"
            badgeBoxSize="0.75em"
          />
        </div>
      </HoverCardTrigger>
      <HoverCardContent className="w-80 h-80 overflow-auto" align="start">
        <div className="flex flex-col gap-1">
          <h4 className="text-sm font-medium leading-none mb-2">Team Members</h4>
          <MemberList members={members} />
        </div>
      </HoverCardContent>
    </HoverCard>
  )
}
